import React, { useState, useMemo, useEffect, useCallback } from "react";
import { Switch, Route, Redirect, useHistory } from "react-router-dom";
import { Layout, Menu, Breadcrumb, Spin } from "@arco-design/web-react";
import cs from "classnames";
import {
  IconDashboard,
  IconSettings,
  IconExclamationCircle,
  IconUser,
  IconMenuFold,
  IconMenuUnfold,
  IconStorage,
  IconDriveFile,
  IconUserGroup,
  IconBook,
  IconApps,
  IconCalendar,
  IconTool,
  IconDesktop,
  IconIdcard,
  IconHistory,
  IconRecord,
  IconEdit,
  IconMobile,
  IconGithub,
  IconCloud,
  IconStop,
  IconHeart,
  IconMenu,
  IconClockCircle,
  IconFile,
  IconFolder,
  IconHome,
  IconThunderbolt,
  IconSafe,
  IconSearch,
  IconMinus,
  IconCheck,
  IconClose,
  IconInfoCircle,
  IconMessage,
  IconPhone,
  IconEmail,
  IconLocation,
  IconStar,
  IconGift,
  IconDownload,
  IconUpload,
  IconRefresh,
  IconMore,
  IconLock,
  IconCamera,
  IconLink,
  IconTrophy,
  IconFire,
  IconQuestionCircle,
  IconSend,
  IconTags,
  IconPoweroff,
  IconBulb,
  IconCode,
  IconSort,
  IconCompass,
  IconPause,
  IconDelete,
  IconPlus,
  IconExpand,
  IconShrink,
} from "@arco-design/web-react/icon";
import { useSelector } from "react-redux";
import NProgress from "nprogress";
import Navbar from "./components/NavBar";
import Footer from "./components/Footer";
import useLocale from "./utils/useLocale";
import getUrlParams from "./utils/getUrlParams";
import lazyload from "./utils/lazyload";
import { GlobalState } from "./store";
import styles from "./style/layout.module.less";
import {
  normalizeMenuData,
  flattenMenus,
  buildParentMap,
  getFirstMenuPath,
  AppMenu,
} from "@/utils/menu";

const MenuItem = Menu.Item;
const SubMenu = Menu.SubMenu;

const Sider = Layout.Sider;
const Content = Layout.Content;

const iconMap: Record<string, React.ComponentType<any>> = {
  dashboard: IconDashboard,
  "dashboard/workplace": IconCalendar,
  cdk: IconStorage,
  "cdk/index": IconTool,
  "cdk/recycle-log": IconHistory,
  "cdk/access-log": IconRecord,
  "box/index": IconTool,
  game: IconDriveFile,
  "game/index": IconDesktop,
  "game/box": IconTool,
  "system/banned-games": IconStop,
  system: IconSettings,
  "system/setting": IconSettings,
  "system/settings": IconSettings,
  "system/user-management": IconUser,
  "system/menu-management": IconApps,
  "system/role": IconUserGroup,
  "system/loginlog": IconHistory,
  "system/operlog": IconRecord,
  operate: IconTool,
  "operate/appversion": IconMobile,
  "operate/app-version": IconMobile,
  manifest: IconApps,
  "manifest/github": IconGithub,
  "manifest/minio": IconCloud,
  config: IconBook,
  "config/website": IconEdit,
  account: IconIdcard,
  "account/drm": IconIdcard,
  steam: IconUserGroup,
  "steam/info": IconUser,
  member: IconUserGroup,
  "member/index": IconUserGroup,
  "member/points": IconStorage,
  "member/purchase": IconDesktop,
  "member/wish": IconHeart,
  user: IconUser,
  "user/profile": IconIdcard,
};

const elIconAliasMap: Record<string, React.ComponentType<any>> = {
  "el-icon-user": IconUser,
  "el-icon-user-solid": IconUser,
  "el-icon-menu": IconMenu,
  "el-icon-s-grid": IconApps,
  "el-icon-setting": IconSettings,
  "el-icon-s-custom": IconUserGroup,
  "el-icon-time": IconClockCircle,
  "el-icon-document": IconFile,
  "el-icon-folder": IconFolder,
  "el-icon-s-home": IconHome,
  "el-icon-house": IconHome,
  "el-icon-s-platform": IconDesktop,
  "el-icon-s-shop": IconApps,
  "el-icon-s-order": IconFile,
  "el-icon-s-marketing": IconDashboard,
  "el-icon-s-flag": IconFire,
  "el-icon-s-help": IconQuestionCircle,
  "el-icon-s-ticket": IconTags,
  "el-icon-s-unfold": IconExpand,
  "el-icon-s-fold": IconShrink,
  "el-icon-s-operation": IconSettings,
  "el-icon-s-tools": IconTool,
  "el-icon-s-goods": IconThunderbolt,
  "el-icon-s-claim": IconSafe,
  "el-icon-remove": IconStop,
  "el-icon-dashboard": IconDashboard,
  "el-icon-edit": IconEdit,
  "el-icon-delete": IconDelete,
  "el-icon-search": IconSearch,
  "el-icon-plus": IconPlus,
  "el-icon-minus": IconMinus,
  "el-icon-check": IconCheck,
  "el-icon-close": IconClose,
  "el-icon-info": IconInfoCircle,
  "el-icon-warning": IconExclamationCircle,
  "el-icon-message": IconMessage,
  "el-icon-phone": IconPhone,
  "el-icon-email": IconEmail,
  "el-icon-location": IconLocation,
  "el-icon-star": IconStar,
  "el-icon-heart": IconHeart,
  "el-icon-gift": IconGift,
  "el-icon-download": IconDownload,
  "el-icon-upload": IconUpload,
  "el-icon-refresh": IconRefresh,
  "el-icon-more": IconMore,
  "el-icon-lock": IconLock,
  "el-icon-camera": IconCamera,
  "el-icon-link": IconLink,
  "el-icon-trophy": IconTrophy,
  "el-icon-monitor": IconDesktop,
  "el-icon-fire": IconFire,
  "el-icon-question": IconQuestionCircle,
  "el-icon-send": IconSend,
  "el-icon-collection-tag": IconTags,
  "el-icon-switch-button": IconPoweroff,
  "el-icon-notebook": IconBook,
  "el-icon-magic-stick": IconBulb,
  "el-icon-code": IconCode,
  "el-icon-s-data": IconStorage,
  "el-icon-mobile-phone": IconMobile,
  "el-icon-sort": IconSort,
  "el-icon-compass": IconCompass,
  "el-icon-connection": IconCloud,
  "el-icon-video-pause": IconPause,
};

const titleIconMap: Record<string, React.ComponentType<any>> = {
  系统设置: IconSettings,
  APP版本管理: IconMobile,
};

const autoTitleIconPool: React.ComponentType<any>[] = [
  IconDashboard,
  IconSettings,
  IconExclamationCircle,
  IconUser,
  IconStorage,
  IconDriveFile,
  IconUserGroup,
  IconBook,
  IconApps,
  IconCalendar,
  IconTool,
  IconDesktop,
  IconIdcard,
  IconHistory,
  IconRecord,
  IconEdit,
  IconMobile,
];

const titleIconCache = new Map<string, React.ComponentType<any>>();
const usedTitleIcons = new Set<React.ComponentType<any>>();

const normalizeTitle = (title?: string) => title?.trim() || "";

const assignIconForTitle = (title: string) => {
  const normalized = normalizeTitle(title);
  if (!normalized || titleIconCache.has(normalized)) {
    return;
  }
  if (titleIconMap[normalized]) {
    const preset = titleIconMap[normalized];
    titleIconCache.set(normalized, preset);
    usedTitleIcons.add(preset);
    return;
  }
  const nextIcon = autoTitleIconPool.find(
    (IconComp) => !usedTitleIcons.has(IconComp)
  );
  if (!nextIcon) {
    return;
  }
  titleIconCache.set(normalized, nextIcon);
  usedTitleIcons.add(nextIcon);
};

const collectMenuTitles = (menus: AppMenu[], acc = new Set<string>()) => {
  menus.forEach((menu) => {
    const normalized = normalizeTitle(menu.title || menu.key);
    if (normalized) {
      acc.add(normalized);
    }
    if (menu.children && menu.children.length) {
      collectMenuTitles(menu.children, acc);
    }
  });
  return acc;
};

const primeTitleIcons = (menus: AppMenu[]) => {
  titleIconCache.clear();
  usedTitleIcons.clear();
  const titles = Array.from(collectMenuTitles(menus)).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: "base" })
  );
  titles.forEach((title) => assignIconForTitle(title));
};

const getTitleBasedIcon = (title?: string) => {
  const normalized = normalizeTitle(title);
  if (!normalized) return null;
  if (!titleIconCache.has(normalized)) {
    assignIconForTitle(normalized);
  }
  return titleIconCache.get(normalized) || null;
};

const ExceptionPage = lazyload(() => import("./pages/exception/403"));

const getMenuIcon = (menu: AppMenu) => {
  const customIconName = menu.icon?.trim();
  if (customIconName) {
    const aliasKey = customIconName.toLowerCase();
    const AliasIcon =
      elIconAliasMap[customIconName] || elIconAliasMap[aliasKey];
    if (AliasIcon) {
      return <AliasIcon className={styles.icon} />;
    }
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const RawIcons = require("@arco-design/web-react/icon") as Record<
      string,
      any
    >;
    const ArcoIcons =
      RawIcons && typeof RawIcons === "object" && !RawIcons.IconApps
        ? RawIcons.default
        : RawIcons;
    const ArcoIcon = ArcoIcons[customIconName];
    if (ArcoIcon) {
      return <ArcoIcon className={styles.icon} />;
    }
  }

  const TitleIcon = getTitleBasedIcon(menu.title);
  if (TitleIcon) {
    return <TitleIcon className={styles.icon} />;
  }

  const iconKey = (customIconName || menu.key).toLowerCase();
  const IconComponent =
    iconMap[iconKey] ||
    iconMap[menu.key.split("/")[0]] ||
    iconMap[menu.path.replace(/^\//, "")];
  if (IconComponent) {
    return <IconComponent className={styles.icon} />;
  }
  if (customIconName && /^(el-|iconfont|icon-)/i.test(customIconName)) {
    return (
      <i
        className={cs(styles.icon, styles["icon-custom"], customIconName)}
        aria-hidden
      />
    );
  }
  return <IconApps className={styles.icon} />;
};

const renderMenuNodes = (menus: AppMenu[]): React.ReactNode[] => {
  const nodes: React.ReactNode[] = [];
  menus.forEach((menu) => {
    if (menu.hide) {
      if (menu.children) {
        nodes.push(...renderMenuNodes(menu.children));
      }
      return;
    }
    const titleNode = (
      <>
        {getMenuIcon(menu)}
        <span>{menu.title}</span>
      </>
    );
    if (menu.children && menu.children.length) {
      nodes.push(
        <SubMenu key={menu.path} title={titleNode}>
          {renderMenuNodes(menu.children)}
        </SubMenu>
      );
    } else {
      nodes.push(<MenuItem key={menu.path}>{titleNode}</MenuItem>);
    }
  });
  return nodes;
};

function PageLayout() {
  const urlParams = getUrlParams();
  const history = useHistory();
  const pathname = history.location.pathname;
  const locale = useLocale();
  const {
    settings,
    userLoading,
    menuLoading,
    menus: rawMenus = [],
  } = useSelector((state: GlobalState) => state);

  const menuWidth = settings.menuWidth;
  const navbarHeight = 60;
  const showNavbar = settings.navbar && urlParams.navbar !== false;
  const showMenu = settings.menu && urlParams.menu !== false;
  const showFooter = settings.footer && urlParams.footer !== false;

  const menus = useMemo(() => {
    const normalized = normalizeMenuData(rawMenus || []);
    primeTitleIcons(normalized);
    return normalized;
  }, [rawMenus]);
  const flattenedRoutes = useMemo(() => flattenMenus(menus), [menus]);
  const parentMap = useMemo(() => buildParentMap(menus), [menus]);
  const breadcrumbMap = useMemo(() => {
    const map = new Map<string, string[]>();
    flattenedRoutes.forEach((route) => {
      map.set(route.path, route.breadcrumb);
    });
    return map;
  }, [flattenedRoutes]);

  const routeComponents = useMemo(() => {
    return flattenedRoutes
      .map((route) => {
        if (!route.component) {
          return null;
        }
        const componentPath = route.component.replace(/^\/+/, "").trim();
        try {
          const Component = lazyload(() => import(`./pages/${componentPath}`));
          return {
            path: route.path,
            Component,
          };
        } catch (error) {
          console.error(`Failed to load page: ${componentPath}`, error);
          return null;
        }
      })
      .filter(Boolean);
  }, [flattenedRoutes]);

  const routeComponentMap = useMemo(() => {
    const map = new Map<string, any>();
    routeComponents.forEach((route) => {
      map.set(route.path, route.Component);
    });
    return map;
  }, [routeComponents]);

  const defaultRoute = useMemo(() => getFirstMenuPath(menus), [menus]);

  const [breadcrumb, setBreadCrumb] = useState<string[]>([]);
  const [collapsed, setCollapsed] = useState<boolean>(false);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [openKeys, setOpenKeys] = useState<string[]>([]);

  const getOpenKeys = useCallback(
    (currentPath: string) => {
      const keys: string[] = [];
      let parent = parentMap.get(currentPath);
      const visited = new Set<string>();
      while (parent && !visited.has(parent)) {
        keys.unshift(parent);
        visited.add(parent);
        parent = parentMap.get(parent);
      }
      return keys;
    },
    [parentMap]
  );

  useEffect(() => {
    if (pathname === "/" && defaultRoute) {
      history.replace(defaultRoute);
      return;
    }
    const crumbs = breadcrumbMap.get(pathname) || [];
    setBreadCrumb(crumbs);
    setSelectedKeys(pathname ? [pathname] : []);
    setOpenKeys(pathname ? getOpenKeys(pathname) : []);
  }, [pathname, breadcrumbMap, defaultRoute, history, getOpenKeys]);

  const onClickMenuItem = (key: string) => {
    if (key && key !== pathname) {
      const loader = routeComponentMap.get(key);
      const preload = loader?.preload;
      NProgress.start();
      const navigate = () => {
        history.push(key);
        NProgress.done();
      };
      if (typeof preload === "function") {
        preload().then(navigate).catch(navigate);
      } else {
        navigate();
      }
    }
  };

  const toggleCollapse = () => {
    setCollapsed((prev) => !prev);
  };

  const paddingLeft = showMenu
    ? { paddingLeft: collapsed ? 48 : menuWidth }
    : {};
  const paddingTop = showNavbar ? { paddingTop: navbarHeight } : {};
  const paddingStyle = { ...paddingLeft, ...paddingTop };

  const menuNodes = useMemo(() => renderMenuNodes(menus), [menus]);

  const loading = userLoading || menuLoading;

  return (
    <Layout className={styles.layout}>
      <div
        className={cs(styles["layout-navbar"], {
          [styles["layout-navbar-hidden"]]: !showNavbar,
        })}
      >
        <Navbar show={showNavbar} />
      </div>
      {loading ? (
        <Spin className={styles["spin"]} />
      ) : (
        <Layout>
          {showMenu && (
            <Sider
              className={styles["layout-sider"]}
              width={menuWidth}
              collapsed={collapsed}
              onCollapse={setCollapsed}
              trigger={null}
              collapsible
              breakpoint="xl"
              style={paddingTop}
            >
              <div className={styles["menu-wrapper"]}>
                <Menu
                  collapse={collapsed}
                  onClickMenuItem={onClickMenuItem}
                  selectedKeys={selectedKeys}
                  openKeys={openKeys}
                  onClickSubMenu={(_, keys) => setOpenKeys(keys)}
                >
                  {menuNodes}
                </Menu>
              </div>
              <div className={styles["collapse-btn"]} onClick={toggleCollapse}>
                {collapsed ? <IconMenuUnfold /> : <IconMenuFold />}
              </div>
            </Sider>
          )}
          <Layout className={styles["layout-content"]} style={paddingStyle}>
            <div className={styles["layout-content-wrapper"]}>
              {!!breadcrumb.length && (
                <div className={styles["layout-breadcrumb"]}>
                  <Breadcrumb>
                    {breadcrumb.map((node, index) => (
                      <Breadcrumb.Item key={index}>
                        {locale[node] || node}
                      </Breadcrumb.Item>
                    ))}
                  </Breadcrumb>
                </div>
              )}
              <Content>
                <Switch>
                  {routeComponents.map((route) => (
                    <Route
                      key={route.path}
                      exact
                      path={route.path}
                      component={route.Component}
                    />
                  ))}
                  {defaultRoute && (
                    <Route exact path="/">
                      <Redirect to={defaultRoute} />
                    </Route>
                  )}
                  <Route component={ExceptionPage} />
                </Switch>
              </Content>
            </div>
            {showFooter && <Footer />}
          </Layout>
        </Layout>
      )}
    </Layout>
  );
}

export default PageLayout;
